import type { ImageData } from '@/types/content'

/** SEO meta fields used to resolve social share (og:image) URLs */
export interface SocialShareSeoMeta {
  og_image?: string
  ogImage?: string
  twitter_image?: string
  twitterImage?: string
  image?: string
  webpImage?: string
}

const RASTER_IMAGE_EXT = /\.(jpe?g|png|gif)(\?.*)?$/i
const WEBP_IMAGE_EXT = /\.webp(\?.*)?$/i

/**
 * Utility service for handling universal image service data
 */
export class ImageService {
  /**
   * Whether a URL points at a raster format suitable for og:image / twitter:image
   */
  static isRasterImageUrl(url: string | null | undefined): boolean {
    if (!url || typeof url !== 'string') return false
    return RASTER_IMAGE_EXT.test(url.trim())
  }

  /**
   * Derive likely original-format URLs from a WebP CDN URL (PNG first, then JPG)
   */
  static deriveRasterFromWebpCandidates(webpUrl: string): string[] {
    if (!WEBP_IMAGE_EXT.test(webpUrl)) return []

    const queryMatch = webpUrl.match(/(\?.*)$/)
    const query = queryMatch ? queryMatch[1] : ''
    const base = webpUrl.replace(/\.webp(\?.*)?$/i, '')

    return [`${base}.png${query}`, `${base}.jpg${query}`]
  }

  static deriveRasterFromWebp(webpUrl: string): string | null {
    return this.deriveRasterFromWebpCandidates(webpUrl)[0] ?? null
  }

  /**
   * Best URL for social sharing meta tags (Open Graph / Twitter Cards).
   * Prefers API SEO raster URLs and image.sizes.original over WebP src.
   */
  static getSocialShareUrl(
    image?: string | ImageData | null,
    seo?: SocialShareSeoMeta | null,
    fallback?: string | null
  ): string | null {
    const seoMeta = seo || {}

    const rasterFromSeo = this.pickRasterCandidate(
      seoMeta.og_image,
      seoMeta.ogImage,
      seoMeta.twitter_image,
      seoMeta.twitterImage,
      seoMeta.image
    )
    if (rasterFromSeo) return rasterFromSeo

    const originalUrl = this.getOriginalUrl(image)
    if (originalUrl && this.isRasterImageUrl(originalUrl)) return originalUrl

    const rasterFromImage = this.pickRasterCandidate(image)
    if (rasterFromImage) return rasterFromImage

    const derivedFromSeo = this.pickDerivedFromWebp(
      seoMeta.og_image,
      seoMeta.ogImage,
      seoMeta.twitter_image,
      seoMeta.twitterImage
    )
    if (derivedFromSeo) return derivedFromSeo

    const derivedFromImage = this.pickDerivedFromWebp(image)
    if (derivedFromImage) return derivedFromImage

    if (typeof fallback === 'string' && fallback) return fallback

    return null
  }

  private static unwrapImageCandidate(
    value: string | ImageData | null | undefined
  ): string | null {
    if (!value) return null
    if (typeof value === 'string') return value
    if (typeof value === 'object' && 'src' in value) {
      return value.sizes?.original || value.src || null
    }
    return null
  }

  private static pickRasterCandidate(
    ...candidates: Array<string | ImageData | null | undefined>
  ): string | null {
    for (const candidate of candidates) {
      const url = this.unwrapImageCandidate(candidate)
      if (url && this.isRasterImageUrl(url)) return url
    }
    return null
  }

  private static pickDerivedFromWebp(
    ...candidates: Array<string | ImageData | null | undefined>
  ): string | null {
    for (const candidate of candidates) {
      const url = this.unwrapImageCandidate(candidate)
      if (!url || !WEBP_IMAGE_EXT.test(url)) continue
      const derived = this.deriveRasterFromWebp(url)
      if (derived) return derived
    }
    return null
  }
  /**
   * Get the best image URL for a given size
   * @param imageData - The image data (string or ImageData object)
   * @param preferredSize - Optional preferred size key
   * @returns The image URL or null if no image data
   */
  static getImageUrl(
    imageData: string | ImageData | null | undefined,
    preferredSize?: string
  ): string | null {
    if (!imageData) return null

    // Handle string format (backward compatibility)
    if (typeof imageData === 'string') {
      return imageData
    }

    // Handle ImageData format
    if (typeof imageData === 'object' && 'src' in imageData) {
      // Always prefer WebP src for primary images
      if (!preferredSize) return imageData.src

      // Return specific size if available
      return imageData.sizes[preferredSize] || imageData.src
    }

    return null
  }

  /**
   * Get responsive image URLs for different screen sizes
   * @param imageData - The image data
   * @returns Object with mobile, tablet, and desktop URLs
   */
  static getResponsiveImages(
    imageData: string | ImageData | null | undefined
  ): { mobile: string; tablet: string; desktop: string } | null {
    if (!imageData) return null

    // Handle string format (backward compatibility)
    if (typeof imageData === 'string') {
      return {
        mobile: imageData,
        tablet: imageData,
        desktop: imageData
      }
    }

    // Handle ImageData format
    if (typeof imageData === 'object' && 'src' in imageData) {
      return {
        mobile: imageData.sizes['80'] || imageData.src,
        tablet: imageData.sizes['150'] || imageData.src,
        desktop: imageData.sizes['300'] || imageData.src
      }
    }

    return null
  }

  /**
   * Generate srcset attribute for responsive images
   * @param imageData - The image data
   * @returns srcset string for HTML img element
   */
  static getSrcSet(imageData: string | ImageData | null | undefined): string {
    if (!imageData) return ''

    // Handle string format (backward compatibility)
    if (typeof imageData === 'string') {
      return imageData
    }

    // Handle ImageData format
    if (typeof imageData === 'object' && 'src' in imageData && 'sizes' in imageData) {
      const srcsetParts: string[] = []
      
      // Add small size (mobile)
      if (imageData.sizes.small) {
        srcsetParts.push(`${imageData.sizes.small} 600w`)
      }
      
      // Add large size (desktop)
      if (imageData.sizes.large) {
        srcsetParts.push(`${imageData.sizes.large} 1200w`)
      }
      
      // Add original size if available
      // if (imageData.sizes.original) {
      //    srcsetParts.push(`${imageData.sizes.original} 1920w`)
      // }
      
      // Add 500px size for high-quality displays
      if (imageData.sizes['500']) {
        srcsetParts.push(`${imageData.sizes['500']} 1000w`)
      }
      
      // Add 300px size as fallback
      if (imageData.sizes['300']) {
        srcsetParts.push(`${imageData.sizes['300']} 600w`)
      }

      // Fallback to src if no sizes available
      if (srcsetParts.length === 0) {
        srcsetParts.push(imageData.src)
      }
      
      return srcsetParts.join(', ')
    }

    return ''
  }

  /**
   * Generate responsive srcset for detail pages with WebP support
   * @param imageData - The image data
   * @returns srcset string optimized for detail pages
   */
  static getResponsiveSrcSet(imageData: string | ImageData | null | undefined): string {
    if (!imageData) return ''

    // Handle string format (backward compatibility)
    if (typeof imageData === 'string') {
      return imageData
    }

    // Handle ImageData format
    if (typeof imageData === 'object' && 'src' in imageData && 'sizes' in imageData) {
      const srcsetParts: string[] = []
      
      // Add 300px size for small screens (max-width: 600px)
      if (imageData.sizes['300']) {
        srcsetParts.push(`${imageData.sizes['300']} 300w`)
      }
      
      // Add 500px size for medium screens (max-width: 1024px)
      if (imageData.sizes['500']) {
        srcsetParts.push(`${imageData.sizes['500']} 500w`)
      }
      
      // Add original size for large screens (1024px+)
      if (imageData.sizes.original) {
        srcsetParts.push(`${imageData.sizes.original} 1920w`)
      } else if (imageData.src) {
        // Fallback to src if no original size
        srcsetParts.push(`${imageData.src} 1920w`)
      }

      // Fallback to src if no sizes available
      if (srcsetParts.length === 0 && imageData.src) {
        srcsetParts.push(imageData.src)
      }

      return srcsetParts.join(', ')
    }

    return ''
  }

  /**
   * Get original file URL (requires includeImageSource=1)
   * @param imageData - The image data
   * @returns The original file URL or null
   */
  static getOriginalUrl(
    imageData: string | ImageData | null | undefined
  ): string | null {
    if (!imageData) return null

    // Handle string format (backward compatibility)
    if (typeof imageData === 'string') {
      return imageData
    }

    // Handle ImageData format
    if (typeof imageData === 'object' && 'src' in imageData) {
      return imageData.sizes.original || null
    }

    return null
  }

  /**
   * Check if image data is in the new structured format
   * @param imageData - The image data to check
   * @returns True if it's ImageData format
   */
  static isStructuredImage(imageData: string | ImageData | null | undefined): imageData is ImageData {
    return typeof imageData === 'object' && imageData !== null && 'src' in imageData && 'sizes' in imageData
  }

  /**
   * Get optimal image URL for sliders with multiple image fields
   * @param slide - The slide object
   * @returns The optimal image URL
   */
  static getOptimalSliderImage(slide: {
    image?: string | ImageData | null
    imageLarge?: string | ImageData
    image_large?: string | ImageData
    webpImage?: string | ImageData
    webpImageLarge?: string | ImageData
    [key: string]: any // Allow for additional fields from API
  }): string | null {
    // Handle both camelCase and snake_case field names from API
    const imageLarge = slide.imageLarge ?? slide.image_large
    const image = slide.image
    
    // Priority: webpImageLarge > imageLarge > webpImage > image
    // For sliders, prefer 'large' size when available for better quality
    if (slide.webpImageLarge) {
      return this.getImageUrl(slide.webpImageLarge, 'large')
    }
    if (imageLarge) {
      return this.getImageUrl(imageLarge, 'large')
    }
   
    if (slide.webpImage) {
      return this.getImageUrl(slide.webpImage, 'large')
    }
    if (image) {
      return this.getImageUrl(image, 'large')
    }
    
    return null
  }
}

// Standalone function for backward compatibility
export function getImageUrl(
  imageData: string | ImageData | null | undefined,
  preferredSize?: string
): string | null {
  return ImageService.getImageUrl(imageData, preferredSize)
} 